import React from 'react';
import { useLocation } from 'react-router';
import { ToastContainer } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import styled from 'styled-components';

import { Color } from '@assets/constants';
import { Footer } from '@features/home/Footer';
import { Header } from '@features/home/Header';
import { MenuDesktop } from '@features/menu/MenuDesktop';
import { useAppConfig, useAppSelector, useScreenSize } from '@hooks';
import { ScreenSize } from '@models/screen';
import { MaintenancePage } from '@pages';
import { AppRoutes } from '@router';
import { ActivityIndicator } from '@ui/ActivityIndicator';
import { isDev } from '@utils/environment';
import { excludeFooter } from '@utils/excludeFooter';
import { excludeHeader } from '@utils/excludeHeader';

const isAdminRoute = (path: string) => path.includes('/admin-panel');

export const App = () => {
  const location = useLocation();
  const screenSize = useScreenSize();
  const { isLoading } = useAppConfig();
  const appConfig = useAppSelector((state) => state.appConfig);

  const currentPath = location.pathname;
  const isSmallScreen = screenSize === ScreenSize.SMALL;
  const isMediumScreen = screenSize === ScreenSize.MEDIUM;
  const showMenuDesktop =
    !isSmallScreen && !isMediumScreen && !currentPath.includes('/admin-panel');

  const excludeFooterArray = ['/admin-panel'];
  if (isSmallScreen) {
    excludeFooterArray.push('/products');
  }

  if (isLoading) {
    return (
      <ActivityIndicatorWrapper>
        <ActivityIndicator size={200} color={Color.ACCENT} />
      </ActivityIndicatorWrapper>
    );
  }

  if (appConfig.isInMaintenanceMode && !isAdminRoute(currentPath) && !isDev()) {
    return <MaintenancePage />;
  }

  return (
    <>
      <StyledToast
        position={'top-center'}
        autoClose={2000}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        hideProgressBar={true}
        stacked
      />
      <AppContainer>
        <DesktopLayout>
          {showMenuDesktop && <MenuDesktop />}
          <Content>
            {excludeHeader(currentPath) && <Header />}
            <AppRoutes />
          </Content>
        </DesktopLayout>
        {excludeFooter(currentPath, excludeFooterArray) && <Footer />}
      </AppContainer>
    </>
  );
};

const StyledToast = styled(ToastContainer)`
  .Toastify__toast {
    border-radius: 30px;
    cursor: pointer;
  }
`;

const DesktopLayout = styled.div`
  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    width: 100%;
  }
`;

const Content = styled.div`
  @media (min-width: 768px) {
    width: 800px;
  }

  @media (min-width: 1366px) {
    width: 1000px;
  }

  @media (min-width: 1600px) {
    width: 1200px;
  }
`;

const ActivityIndicatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100svh;

  @media (min-width: 768px) {
    align-items: center;
  }
`;
